import { createContext, useContext, useMemo, type PropsWithChildren } from 'react';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import {
  resolveProductTaxonomyCopy,
  resolveProductTaxonomyTerm,
  type ProductTaxonomyCopyKey,
  type ProductTaxonomyStage,
  type ProductTaxonomyTermReference,
  type ProductTaxonomyVariant,
} from './catalog';
import { resolveProductTaxonomyRoute, type ProductTaxonomyRouteKey } from './routes';

export {
  resolveProductTaxonomyCopy,
  resolveProductTaxonomyTerm,
  type ProductTaxonomyCopyKey,
  type ProductTaxonomyStage,
  type ProductTaxonomyTermFormKey,
  type ProductTaxonomyTermKey,
  type ProductTaxonomyTermReference,
  type ProductTaxonomyVariant,
} from './catalog';
export {
  productTaxonomyCopyCatalog,
  productTaxonomyRouteCatalog,
  productTaxonomySideNavItemIdMap,
  productTaxonomyTermCatalog,
} from './renames';
export {
  normalizeJourneysPathname,
  resolveProductTaxonomyRoute,
  type ProductTaxonomyRouteKey,
  type ProductTaxonomyRouteTemplate,
} from './routes';
export {
  getProductTaxonomySideNavPreferenceIds,
  normalizeProductTaxonomySideNavPreferences,
  type SideNavPreferences,
} from './side-nav-preferences';
export type {
  ProductTaxonomyCopyEntry,
  ProductTaxonomyCopyTermReference,
  ProductTaxonomyRenameDefinition,
  ProductTaxonomyRouteEntry,
  ProductTaxonomyTermEntry,
  ProductTaxonomyTermForms,
} from './types';

export type ProductTaxonomy = {
  variant: ProductTaxonomyVariant;
  // True when the rename is in its "final" state: the renamed language is shown
  // without the transitional affordances (nav rename tooltips, "New (Old)" page
  // titles). `variant` is still `future` in this state.
  isFinal: boolean;
  t: (key: ProductTaxonomyCopyKey) => string;
  term: (reference: ProductTaxonomyTermReference) => string;
  route: (key: ProductTaxonomyRouteKey) => string;
};

const ProductTaxonomyContext = createContext<ProductTaxonomy | null>(null);

type ProductTaxonomyProviderProps = PropsWithChildren<{
  stage: ProductTaxonomyStage;
}>;

export function ProductTaxonomyProvider({ children, stage }: ProductTaxonomyProviderProps) {
  const value = useMemo(() => createProductTaxonomy(stage), [stage]);

  return <ProductTaxonomyContext.Provider value={value}>{children}</ProductTaxonomyContext.Provider>;
}

export function useProductTaxonomy(): ProductTaxonomy {
  const context = useContext(ProductTaxonomyContext);
  const isRenameEnabled = useFeatureFlag(AccountFeatureFlag.JourneysTaxonomyRename);
  const isRenameFinalEnabled = useFeatureFlag(AccountFeatureFlag.JourneysTaxonomyRenameFinal);
  // The "final" flag implies the rename is on and wins over the transitional flag.
  const stage: ProductTaxonomyStage = isRenameFinalEnabled ? 'final' : isRenameEnabled ? 'future' : 'current';

  return useMemo(() => context ?? createProductTaxonomy(stage), [context, stage]);
}

export function createProductTaxonomy(stage: ProductTaxonomyStage): ProductTaxonomy {
  return {
    variant: stage === 'final' ? 'future' : stage,
    isFinal: stage === 'final',
    t: (key) => resolveProductTaxonomyCopy(key, stage),
    term: (reference) => resolveProductTaxonomyTerm(reference, stage),
    route: (key) => resolveProductTaxonomyRoute(key, stage),
  };
}
